import { chain, compose, isEmpty, pluck, result, isFunction, find } from 'underscore';
import React, { useCallback, useMemo, useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { getBulkApiErrors, getODataErrors } from '@bingads-webui-component-legacy/api-errors-campaign';
import { getRoutePathById } from '@bingads-webui-react/with-site-map';
import { ContentFooter } from '@bingads-webui-campaign-react/content-footer';
import { ProgressiveLoadingBlock } from '@bingads-webui-campaign-react/shell-ui-common';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { withI18nConsumer } from '@bingads-webui-react/i18n-context';
import { AlertHelper } from '@bingads-webui-campaign/alert-helper';
import { perfMarkerPropTypes } from '@bingads-webui/performance-marker';
import { PerformanceMarkerProvider } from '@bingads-webui-campaign-react/performance-marker-provider';
import { withUiBlock } from '@bingads-webui/uiblocker';
import { EditorialAppealRequestPanel, editorialAppealAggregator, requestAppeal } from '@bingads-webui-campaign-react/editorial-appeal';
import cacheManager from '@bingads-webui-campaign-legacy/cache-manager/index';
import { AssetGroupsGrid } from './grid';
import { getStyle } from './style';
import { Status } from './constants/status';
import { updateAssetGroup, bulkUpdateAssetGroups } from './api/update-entity';
import { getAssetGroupsForCampaign, getEditorialReasonsForAssetGroup, requestAppealAllForParentEntity, requestAppealByIDsForParentEntity } from './api/grid-data';

const AssetGroupsPage = (props) => {
  const {
    classes,
    i18n,
    scope,
    siteMap,
    perfMarker,
    permissions,
    preferenceService,
    serverContext,
    currentCampaign,
    changeLayout,
  } = props;

  const {
    values: {
      cid, aid,
    },
  } = scope;

  const isReadOnlyUser = useMemo(() => permissions.IsReadOnlyUser || serverContext.CurrentAccount.Status === 32, [permissions.IsReadOnlyUser, serverContext.CurrentAccount.Status]); // 32 is InActive

  const containerRef = useRef();

  const onCreateAssetGroup = useCallback(() => {
    if (currentCampaign) {
      const { CampaignSettings } = currentCampaign;

      const AppSettings = find(CampaignSettings, settings => (settings['@odata.type'] === '#Model.AppSetting'));

      const isMobileAppCampaign = AppSettings && (AppSettings.AppStore === 'AppleAppStore' || AppSettings.AppStore === 'GoogleAppStore');

      const isUnifiedAppCampaign = AppSettings && AppSettings.IsUniversalAppCampaign;

      if ((isMobileAppCampaign || isUnifiedAppCampaign) && permissions.dynamic.UnifiedAppAds) {
        const route = getRoutePathById(siteMap, 'unified-appads-asset-group-create');
        scope.remove(['assetGroupId']);
        scope.save(route);
      } else {
        const route = getRoutePathById(siteMap, 'appads-asset-group-create');
        scope.remove(['assetGroupId']);
        scope.save(route);
      }
    }
  }, [scope, siteMap, currentCampaign, permissions]);

  const onEditAssetGroup = useCallback((assetGroupId) => {
    if (currentCampaign) {
      const { CampaignSettings } = currentCampaign;

      const AppSettings = find(CampaignSettings, settings => (settings['@odata.type'] === '#Model.AppSetting'));

      const isMobileAppCampaign = AppSettings && (AppSettings.AppStore === 'AppleAppStore' || AppSettings.AppStore === 'GoogleAppStore');

      const isUnifiedAppCampaign = AppSettings && AppSettings.IsUniversalAppCampaign;

      if ((isMobileAppCampaign || isUnifiedAppCampaign) && permissions.dynamic.UnifiedAppAds) {
        const route = getRoutePathById(siteMap, 'unified-appads-asset-group-edit');
        scope.values.assetGroupId = assetGroupId;
        scope.save(route);
      } else {
        const route = getRoutePathById(siteMap, 'appads-asset-group-edit');
        scope.values.assetGroupId = assetGroupId;
        scope.save(route);
      }
    }
  }, [scope, siteMap, currentCampaign, permissions]);

  const onNavigateToAssetsPage = useCallback((assetGroupId) => {
    const route = getRoutePathById(siteMap, 'unified-app-ads-assets');
    scope.values.assetGroupId = assetGroupId;
    scope.save(route);
  }, [siteMap, scope]);

  const isUnifiedAppAds = useMemo(() => {
    const { CampaignSettings } = currentCampaign;

    const AppSettings = find(CampaignSettings, settings => (settings['@odata.type'] === '#Model.AppSetting'));

    return AppSettings && permissions.dynamic.UnifiedAppAds && AppSettings.IsUniversalAppCampaign;
  }, [permissions, currentCampaign]);

  const isMobileAppCampaign = useMemo(() => {
    const { CampaignSettings } = currentCampaign;
    const AppSettings = find(CampaignSettings, settings => (settings['@odata.type'] === '#Model.AppSetting'));
    return AppSettings && (AppSettings.AppStore === 'AppleAppStore' || AppSettings.AppStore === 'GoogleAppStore');
  }, [currentCampaign]);

  const onGenericError = useCallback(() => {
    const alertHelper = new AlertHelper({ i18n });
    alertHelper.showError(i18n.getString(_TL_('We\'ve encountered an error and can\'t continue. Please contact support for further assistance.')));
  }, [i18n]);

  const onBulkUpdateApiError = useCallback((bulkResponse) => {
    const errors = result(bulkResponse, 'errors', []);
    if (isEmpty(errors)) {
      onGenericError();
    } else {
      const alertHelper = new AlertHelper({ i18n });
      alertHelper.showError(pluck(getBulkApiErrors(errors, 'Asset Group Bulk Update'), 'message'));
    }
  }, [i18n, onGenericError]);

  const onBulkUpdateManyApiError = useCallback((bulkResponse) => {
    const errors = chain(bulkResponse)
      .map(response => result(response, 'errors'))
      .compact()
      .map(error => pluck(getBulkApiErrors(error, 'Asset Group Bulk Update Many'), 'message'))
      .flatten()
      .uniq(true)
      .value()
      .join('\n');

    if (!isEmpty(errors)) {
      const alertHelper = new AlertHelper({ i18n });
      alertHelper.showError(errors);
    }
  }, [i18n]);

  const onEditAssetGroupStatus = useCallback((assetGroupId, status) => (
    updateAssetGroup({
      campaignId: scope.values.campaignId,
      assetGroupId,
      updateValues: { Status: status },
    })
      .catch(onBulkUpdateApiError)
      .finally(withUiBlock(containerRef.current))
  ), [onBulkUpdateApiError, scope.values.campaignId]);

  const onBulkEditAssetGroupsStatus = useCallback((assetGroups, status) => {
    // eslint-disable-next-line no-alert
    if (status === Status.Deleted && !confirm(i18n.getString(_TL_('Are you sure you want to delete selected asset groups?')))) {
      return Promise.resolve({ cancelled: true });
    }

    return bulkUpdateAssetGroups({
      assetGroups,
      campaignId: scope.values.campaignId,
      updateValues: { Status: status },
    })
      .then(onBulkUpdateManyApiError) // BulkUpdateMany will not reject in case of entity errors, need to check errors in then
      .catch(onGenericError)
      .finally(withUiBlock(containerRef.current));
  }, [i18n, onBulkUpdateManyApiError, onGenericError, scope.values.campaignId]);

  const onInlineEditAssetGroup = useCallback((assetGroupId, updateValues) => (
    updateAssetGroup({
      campaignId: scope.values.campaignId,
      assetGroupId,
      updateValues,
    })
      .finally(withUiBlock(containerRef.current))
  ), [scope.values.campaignId]);

  const appealOptions = useMemo(() => ({
    cid,
    aid,
    permissions,
    controller: 'AssetGroup',
    getAllEntitiesForParentEntity: getAssetGroupsForCampaign,
    getEditorialReasonsByIDsForParentEntity: getEditorialReasonsForAssetGroup,
    requestAppealAllForParentEntity,
    requestAppealByIDsForParentEntity,
  }), [aid, cid, permissions]);

  const [appealFormOpen, setAppealFormOpen] = useState(false);
  const [appealParams, setAppealParams] = useState(null);
  const [appealDataPromise, setAppealDataPromise] = useState(null);
  const [appealDataCallbacks, setAppealDataCallbacks] = useState({});

  const onDoneRequestingException = useCallback(() => {
    cacheManager.invalidate('AssetGroup');
    if (isFunction(appealDataCallbacks.onDone)) {
      appealDataCallbacks.onDone();
    }
  }, [appealDataCallbacks]);

  const onRequestException = useCallback((newAppealParams, { onDone }) => {
    const aggregatedRejectReasonsPromise = editorialAppealAggregator(newAppealParams);
    setAppealParams(newAppealParams);
    setAppealDataCallbacks({ onDone });
    setAppealDataPromise(aggregatedRejectReasonsPromise);
    setAppealFormOpen(true);
  }, []);

  const onAppealSubmit = useCallback((appealData, appealIds) => {
    setAppealFormOpen(false);
    setAppealDataPromise(null);
    setAppealDataCallbacks({});

    requestAppeal(appealParams, appealData, appealIds)
      .then(onDoneRequestingException)
      .catch((errors) => {
        const errorMessages = pluck(getODataErrors(errors, permissions), 'message');

        if (!isEmpty(errorMessages)) {
          const alertHelper = new AlertHelper({ i18n });
          alertHelper.showError(errorMessages.join(';\n'));
        }
      })
      .finally(withUiBlock(containerRef.current));
  }, [onDoneRequestingException, i18n, permissions, appealParams]);

  const onAppealCancel = useCallback(() => {
    setAppealFormOpen(false);
    setAppealDataPromise(null);
    setAppealDataCallbacks({});
  }, []);

  useEffect(() => {
    changeLayout({ tab: { showDatePicker: true, disableCompare: true } }, true);
  }, [changeLayout]);

  return (
    <PerformanceMarkerProvider
      perfMarker={perfMarker}
      immediateDone={false}
    >
      <div className={classNames(['appads-asset-groups-page', 'small-padding-left', classes.pageContainer])} ref={containerRef}>
        <React.Suspense fallback={<ProgressiveLoadingBlock />}>
          <section className={classNames(['appads-asset-groups-grid', classes.gridContainer])}>
            <AssetGroupsGrid
              classes={classes}
              i18n={i18n}
              isReadOnlyUser={isReadOnlyUser}
              perfMarker={perfMarker}
              permissions={permissions}
              preferenceService={preferenceService}
              scope={scope}
              serverContext={serverContext}
              appealOptions={appealOptions}
              onCreateAssetGroup={onCreateAssetGroup}
              onEditAssetGroup={onEditAssetGroup}
              onEditAssetGroupStatus={onEditAssetGroupStatus}
              onBulkEditAssetGroupsStatus={onBulkEditAssetGroupsStatus}
              onRequestException={onRequestException}
              onInlineEditAssetGroup={onInlineEditAssetGroup}
              onGenericError={onGenericError}
              onNavigateToAssetsPage={onNavigateToAssetsPage}
              isUnifiedAppAds={isUnifiedAppAds}
              isMobileAppCampaign={isMobileAppCampaign}
            />
          </section>
          <ContentFooter />
        </React.Suspense>
      </div>
      {appealFormOpen && appealDataPromise &&
        <EditorialAppealRequestPanel
          i18n={i18n}
          title={i18n.getString(_TL_('Request exception'))}
          appealDataPromise={appealDataPromise}
          onSave={onAppealSubmit}
          onCancel={onAppealCancel}
        />
      }
    </PerformanceMarkerProvider>
  );
};

AssetGroupsPage.propTypes = {
  classes: PropTypes.shape({
    gridContainer: PropTypes.string.isRequired,
    pageContainer: PropTypes.string.isRequired,
  }).isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  perfMarker: perfMarkerPropTypes.isRequired,
  permissions: PropTypes.shape({
    IsReadOnlyUser: PropTypes.bool,
  }).isRequired,
  preferenceService: PropTypes.shape({
    findByNameAtUserLevel: PropTypes.func.isRequired,
    setAtUserLevel: PropTypes.func.isRequired,
  }).isRequired,
  scope: PropTypes.shape({
    remove: PropTypes.func,
    save: PropTypes.func,
    values: PropTypes.shape({
      aid: PropTypes.number,
      cid: PropTypes.number,
      campaignId: PropTypes.number,
      assetGroupId: PropTypes.number,
      assetGroupTableMode: PropTypes.bool,
    }),
  }).isRequired,
  serverContext: PropTypes.shape({
    CurrentAccount: PropTypes.shape({
      Status: PropTypes.number,
    }),
  }).isRequired,
  siteMap: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
  })).isRequired,
};

export const StyledAppAdsAssetGroupsPage = compose(
  withI18nConsumer,
  WrappedComponent => withDefaultStyles(WrappedComponent, getStyle)
)(AssetGroupsPage);
